/**
 * FunnelSidebar.jsx — vertical underwriting funnel (5 stages × sub-tools).
 *
 * Pure presentational — receives state + callbacks from UnderwritingWorkspace.
 * Each substep shows its status (idle / running / passed / failed / paused),
 * and the active step exposes any inline inputs needed before the tool can run
 * (director picker, homeowner toggle, bank-stmt file picker, offer form).
 */
import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  CheckCircle2,
  Lock,
  Loader2,
  AlertOctagon,
  PauseCircle,
  Play,
  Building2,
  Sparkles,
  CreditCard,
  Wallet,
  UserSearch,
  Network as NetIcon,
  Globe,
  ShieldCheck,
  ChevronRight,
  ChevronDown,
  Paperclip,
  Download,
  Calculator,
  Search,
  CheckSquare,
} from "lucide-react";


// Stage definitions — keep tight, the orchestrator decides what fires when
const STAGES = [
  {
    key: "eligibility",
    label: "1. Eligibility",
    desc:  "Confirm the company exists, trades long enough, no hard-decline flags",
    icon:  CheckSquare,
    substeps: [
      { stage: "company_search",   label: "Find on Companies House", icon: Search },
      { stage: "company_validate", label: "CH basics (trading / officers / SIC)", icon: Building2 },
      { stage: "eligibility",      label: "Eligibility (director age / status / hard fails)", icon: Sparkles },
    ],
  },
  {
    key: "affordability",
    label: "2. Affordability",
    desc:  "Full affordability assessment from bank statements",
    icon:  Wallet,
    substeps: [
      { stage: "bs_analysis", label: "Analyse bank statements", icon: Paperclip, custom: true },
    ],
  },
  {
    key: "credit",
    label: "3. Credit",
    desc:  "Company score + personal credit on the lead director",
    icon:  CreditCard,
    substeps: [
      { stage: "credit_company",  label: "Company credit (Creditsafe)", icon: CreditCard },
      { stage: "director_credit", label: "Director personal credit",     icon: UserSearch },
    ],
  },
  {
    key: "network",
    label: "4. Network & Reputation",
    desc:  "Linked companies + online reputation",
    icon:  NetIcon,
    substeps: [
      { stage: "director_network", label: "Network (North Data)",      icon: NetIcon },
      { stage: "reputation",       label: "Reputation (SerpAPI)",      icon: Globe },
    ],
  },
  {
    key: "kyc",
    label: "5. KYC",
    desc:  "Digital ID + AML",
    icon:  ShieldCheck,
    substeps: [
      { stage: "bynn_check", label: "Bynn ID + AML", icon: ShieldCheck },
    ],
  },
  {
    key: "offer",
    label: "6. Offer",
    desc:  "Offer sizing — locked until affordability + credit are done or overridden",
    icon:  Calculator,
    substeps: [
      { stage: "offer", label: "Offer calculator", icon: Calculator, custom: true },
    ],
  },
];

// Offer stays locked until every one of these has run (or failed and been overridden)
const OFFER_PREREQS = ["bs_analysis", "credit_company", "director_credit"];

// Convenience: get the highest stage that has any substep that's run
function highestStageRun(toolsRun) {
  for (let i = STAGES.length - 1; i >= 0; i--) {
    if (STAGES[i].substeps.some((s) => toolsRun.has(s.stage))) return i;
  }
  return -1;
}

// Are all sweep-eligible (non-custom) substeps already run?
function areAllAutoStagesRun(toolsRun) {
  for (const stg of STAGES) {
    for (const sub of stg.substeps) {
      if (sub.custom) continue;        // custom substeps need user input
      if (!toolsRun.has(sub.stage)) return false;
    }
  }
  return true;
}


export default function FunnelSidebar({
  // state
  orchestratorBusy,
  hasStarted,
  confirmedCrn,
  confirmedName,
  searchMatches,
  pendingCrnPick,
  setPendingCrnPick,
  activeOfficers,
  chosenDirector,
  setChosenDirector,
  isHomeowner,
  setIsHomeowner,
  toolsRun,           // Set<stage>
  runningTool,        // current stage or null
  failedSteps,        // Set<stage>
  pausedAt,           // 'awaiting_confirm' | 'awaiting_director' | 'awaiting_bs_input' | 'awaiting_offer_input' | 'awaiting_user' | null
  files,              // Monday files
  dealName,           // default query for company_search
  // callbacks
  onStart,
  onContinue,         // resume sweep from current state
  onConfirmCompany,
  onRunStage,         // (stage, args?) => void  — manual re-run
  onBsAnalyseFromMonday,
  onBsAnalyseUpload,
  onCalcOffer,
  onDownloadBsCsv,
}) {
  const reachedStageIdx = highestStageRun(toolsRun);
  const offerUnlocked = OFFER_PREREQS.every((s) => toolsRun.has(s) || failedSteps.has(s));
  // A stage is "unlocked" if (it's stage 0) OR (any of its substeps have run) OR (the previous stage has any substep done)
  const stageUnlocked = (i) => {
    if (STAGES[i].key === "offer") return hasStarted && offerUnlocked;
    if (i === 0) return hasStarted;
    return i <= reachedStageIdx + 1 && hasStarted;
  };

  // Show a Continue button when: started, no inline gate active, no fail, sweep incomplete.
  const showContinue =
    hasStarted &&
    !orchestratorBusy &&
    !runningTool &&
    failedSteps.size === 0 &&
    !pausedAt &&
    // there exists an auto-flow stage not yet run
    !areAllAutoStagesRun(toolsRun);

  return (
    <div className="h-full flex flex-col overflow-hidden" data-testid="uw-funnel-sidebar">
      {/* Pinned confirmed banner */}
      {confirmedCrn && (
        <div
          className="mb-3 flex items-center gap-2 p-2.5 rounded-lg border border-green-200 bg-green-50 shrink-0"
          data-testid="uw-funnel-confirmed"
        >
          <CheckCircle2 className="w-4 h-4 text-green-700 shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="text-xs font-semibold text-green-900 truncate">{confirmedName}</div>
            <div className="text-[10px] text-green-700 font-mono">#{confirmedCrn}</div>
          </div>
        </div>
      )}

      {/* Start Analysis CTA — only shown before any tool has fired */}
      {!hasStarted && (
        <Card className="mb-3 shrink-0 border-violet-200 bg-gradient-to-br from-violet-50 to-blue-50">
          <CardContent className="p-4 text-center space-y-3">
            <Sparkles className="w-7 h-7 mx-auto text-violet-500" />
            <div>
              <div className="font-semibold text-sm">Ready to underwrite</div>
              <p className="text-xs text-muted-foreground mt-1">
                Click Start and I&apos;ll work through eligibility, affordability, credit, network &amp; KYC —
                pausing only for your input when I need it.
              </p>
            </div>
            <Button
              onClick={onStart}
              disabled={orchestratorBusy}
              className="w-full"
              data-testid="uw-funnel-start"
            >
              {orchestratorBusy ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Starting…</>
              ) : (
                <><Play className="w-4 h-4 mr-2" /> Start analysis</>
              )}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Continue Analysis CTA — visible when sweep was interrupted (page reload, manual run) */}
      {showContinue && (
        <Card className="mb-3 shrink-0 border-blue-200 bg-blue-50">
          <CardContent className="p-3">
            <Button
              onClick={onContinue}
              disabled={orchestratorBusy}
              className="w-full"
              variant="default"
              data-testid="uw-funnel-continue"
            >
              <Play className="w-4 h-4 mr-2" />
              Continue analysis
            </Button>
            <p className="text-[10px] text-center text-muted-foreground mt-1.5">
              Resumes from the next un-run step.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Stages */}
      <div className="flex-1 overflow-y-auto pr-1 space-y-2" data-testid="uw-funnel-stages">
        {STAGES.map((stg, idx) => {
          const unlocked = stageUnlocked(idx);
          const StageIcon = stg.icon;
          const stageRunCount = stg.substeps.filter((s) => toolsRun.has(s.stage)).length;
          // company_search failures show their own inline retry input — don't count toward "FAIL" or red chat-hint
          const failingSubs = stg.substeps.filter((s) => failedSteps.has(s.stage) && s.stage !== "company_search");
          const stageHasFail = failingSubs.length > 0;
          const stageDone = stageRunCount === stg.substeps.length && !stageHasFail;

          return (
            <div key={stg.key} className={`border rounded-lg ${unlocked ? "bg-white" : "bg-slate-50/60 opacity-60"}`} data-testid={`uw-stage-${stg.key}`}>
              {/* Stage header */}
              <div className="flex items-center gap-2 p-3 border-b">
                <StageIcon className={`w-4 h-4 ${stageDone ? "text-green-600" : stageHasFail ? "text-red-600" : "text-slate-500"}`} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold leading-tight">{stg.label}</div>
                  <div className="text-[10px] text-muted-foreground leading-tight mt-0.5">{stg.desc}</div>
                </div>
                {!unlocked ? (
                  <Lock className="w-3.5 h-3.5 text-muted-foreground" />
                ) : stageHasFail ? (
                  <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 text-[10px]">FAIL</Badge>
                ) : stageDone ? (
                  <CheckCircle2 className="w-4 h-4 text-green-600" />
                ) : (
                  <Badge variant="outline" className="text-[10px]">{stageRunCount}/{stg.substeps.length}</Badge>
                )}
              </div>

              {/* Offer lock hint — which prereqs are still outstanding */}
              {stg.key === "offer" && hasStarted && !unlocked && (
                <div className="px-3 py-2 text-[10px] text-muted-foreground" data-testid="uw-offer-locked-hint">
                  Locked — still needed:{" "}
                  {OFFER_PREREQS.filter((s) => !(toolsRun.has(s) || failedSteps.has(s)))
                    .map((s) => ({ bs_analysis: "affordability", credit_company: "company credit", director_credit: "director credit" }[s] || s))
                    .join(", ")}
                </div>
              )}

              {/* Substeps */}
              {unlocked && (
                <div className="p-2 space-y-1">
                  {stg.substeps.map((sub) => {
                    const SubIcon = sub.icon;
                    const ran = toolsRun.has(sub.stage);
                    const running = runningTool === sub.stage;
                    // company_search has its own inline retry UI — don't render as red FAIL row
                    const failed = failedSteps.has(sub.stage) && sub.stage !== "company_search";
                    const isCompanySearchNeedsInput = sub.stage === "company_search" && !confirmedCrn && hasStarted;
                    return (
                      <div key={sub.stage} className="text-xs">
                        <div
                          className={`flex items-center gap-2 px-2 py-1.5 rounded ${
                            failed
                              ? "bg-red-50"
                              : ran
                              ? "bg-green-50/50"
                              : ""
                          }`}
                          data-testid={`uw-sub-${sub.stage}`}
                        >
                          <SubIcon className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                          <span className="flex-1 min-w-0 truncate">{sub.label}</span>
                          {running ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin text-blue-500 shrink-0" />
                          ) : failed ? (
                            <AlertOctagon className="w-3.5 h-3.5 text-red-500 shrink-0" />
                          ) : ran && !isCompanySearchNeedsInput ? (
                            <CheckCircle2 className="w-3.5 h-3.5 text-green-600 shrink-0" />
                          ) : isCompanySearchNeedsInput ? (
                            <PauseCircle className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                          ) : pausedAt && _pauseMatchesStage(pausedAt, sub.stage) ? (
                            <PauseCircle className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                          ) : (
                            <span className="w-3.5 h-3.5 rounded-full border border-slate-300 shrink-0" />
                          )}
                        </div>

                        {/* Inline UI when paused at this substep */}
                        {/* awaiting_confirm OR step ran/failed without a confirmed company yet — editable search + match picker */}
                        {sub.stage === "company_search" && !confirmedCrn && hasStarted && (
                          <InlineConfirmGate
                            dealName={dealName}
                            matches={searchMatches}
                            pending={pendingCrnPick}
                            setPending={setPendingCrnPick}
                            onConfirm={onConfirmCompany}
                            onSearch={(q) => onRunStage("company_search", { q })}
                            busy={runningTool === "company_search"}
                            failed={failedSteps.has("company_search")}
                          />
                        )}

                        {/* awaiting_director — pick director + homeowner for eligibility */}
                        {sub.stage === "eligibility" && pausedAt === "awaiting_director" && (
                          <InlineDirectorPicker
                            officers={activeOfficers}
                            chosen={chosenDirector}
                            setChosen={setChosenDirector}
                            isHomeowner={isHomeowner}
                            setIsHomeowner={setIsHomeowner}
                            onContinue={() => onRunStage("eligibility")}
                            busy={running}
                          />
                        )}

                        {/* bs_analysis — pick Monday files or upload */}
                        {sub.stage === "bs_analysis" && (pausedAt === "awaiting_bs_input" || (!ran && unlocked)) && (
                          <InlineBsAnalysis
                            files={files}
                            onAnalyseFromMonday={onBsAnalyseFromMonday}
                            onAnalyseUpload={onBsAnalyseUpload}
                            busy={running}
                          />
                        )}

                        {/* bs_analysis done — export the workings for audit */}
                        {sub.stage === "bs_analysis" && ran && (
                          <button
                            onClick={onDownloadBsCsv}
                            className="ml-7 mt-1 inline-flex items-center gap-1 text-[10px] text-blue-600 hover:text-blue-800 hover:underline"
                            data-testid="uw-bs-csv-btn"
                          >
                            <Paperclip className="w-3 h-3" /> Export workings (CSV)
                          </button>
                        )}

                        {/* offer calculator inline form */}
                        {sub.stage === "offer" && (pausedAt === "awaiting_offer_input" || (!ran && unlocked)) && (
                          <InlineOfferForm
                            isHomeowner={isHomeowner}
                            onCalculate={onCalcOffer}
                            busy={running}
                          />
                        )}
                      </div>
                    );
                  })}

                  {/* Failed-stage helper: invite user to chat to override */}
                  {stageHasFail && (
                    <div className="text-[10px] text-red-700 px-2 mt-1 italic">
                      Hard-fail flagged — reply in chat with overriding info if applicable.
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Files panel (collapsible) */}
      <FilesPanel files={files} />
    </div>
  );
}


// ─── Sub-components ───────────────────────────────────────────────────────

function _pauseMatchesStage(pausedAt, stage) {
  if (pausedAt === "awaiting_confirm" && stage === "company_search") return true;
  if (pausedAt === "awaiting_director" && stage === "eligibility") return true;
  if (pausedAt === "awaiting_bs_input" && stage === "bs_analysis") return true;
  if (pausedAt === "awaiting_offer_input" && stage === "offer") return true;
  return false;
}


function InlineConfirmGate({ dealName, matches, pending, setPending, onConfirm, onSearch, busy, failed }) {
  const [query, setQuery] = useState(dealName || "");
  // Keep input in sync if dealName arrives later
  useEffect(() => {
    if (!query && dealName) setQuery(dealName);
  }, [dealName, query]);

  const triggerSearch = () => {
    const q = (query || "").trim();
    if (!q || busy) return;
    onSearch(q);
  };

  return (
    <div className="mt-1 ml-5 mb-2 p-2 border rounded bg-violet-50/30 space-y-1.5" data-testid="uw-inline-confirm">
      <div className="text-[10px] font-medium text-violet-700">
        Search Companies House:
      </div>
      <div className="flex gap-1.5">
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); triggerSearch(); } }}
          placeholder="Company name or CRN"
          disabled={busy}
          className="h-7 text-[11px]"
          data-testid="uw-confirm-query"
        />
        <Button onClick={triggerSearch} disabled={busy || !query.trim()} size="sm" className="h-7 text-[10px] shrink-0" data-testid="uw-confirm-search-again">
          {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Search className="w-3 h-3 mr-1" />}
          Search
        </Button>
      </div>

      {matches.length > 0 ? (
        <>
          <div className="text-[10px] font-medium text-violet-700 pt-1">
            Pick the company &amp; confirm:
          </div>
          <div className="space-y-1 max-h-40 overflow-auto">
            {matches.map((m) => (
              <button
                key={m.company_number}
                onClick={() => setPending(m.company_number)}
                className={`w-full text-left text-[11px] px-2 py-1 rounded border ${
                  pending === m.company_number
                    ? "border-violet-400 bg-white shadow-sm ring-1 ring-violet-200"
                    : "border-transparent hover:bg-white"
                }`}
                data-testid={`uw-confirm-match-${m.company_number}`}
              >
                <div className="font-medium">{m.company_name || m.title}</div>
                <div className="text-muted-foreground">
                  #{m.company_number} · {m.company_status}
                  {m.date_of_creation && ` · since ${m.date_of_creation}`}
                </div>
              </button>
            ))}
          </div>
          <Button onClick={onConfirm} disabled={!pending || busy} size="sm" className="w-full h-7 text-[10px]" data-testid="uw-confirm-btn">
            <CheckCircle2 className="w-3 h-3 mr-1" /> Confirm
          </Button>
        </>
      ) : (
        <div className="text-[10px] text-muted-foreground italic pt-1">
          {failed
            ? "Search failed or timed out — edit the name and try again."
            : "No matches yet — edit the name above and click Search."}
        </div>
      )}
    </div>
  );
}


function InlineDirectorPicker({ officers, chosen, setChosen, isHomeowner, setIsHomeowner, onContinue, busy }) {
  return (
    <div className="mt-1 ml-5 mb-2 p-2 border rounded bg-blue-50/30 space-y-1.5" data-testid="uw-inline-director">
      <div className="text-[10px] font-medium text-blue-700">
        Pick the lead director:
      </div>
      <div className="space-y-1 max-h-32 overflow-auto">
        {(officers || []).map((o) => (
          <button
            key={o.name}
            onClick={() => setChosen(o.name)}
            className={`w-full text-left text-[11px] px-2 py-1 rounded border ${
              chosen === o.name ? "border-blue-400 bg-white shadow-sm" : "border-transparent hover:bg-white"
            }`}
            data-testid={`uw-director-${(o.name || "").replace(/[\s,]+/g, '-')}`}
          >
            <div className="font-medium">{o.name}</div>
            <div className="text-muted-foreground">{o.officer_role}</div>
          </button>
        ))}
      </div>
      <label className="flex items-center gap-1.5 text-[11px] cursor-pointer pt-1">
        <Checkbox checked={isHomeowner} onCheckedChange={setIsHomeowner} data-testid="uw-homeowner-toggle" />
        Director is a homeowner
      </label>
      <Button onClick={onContinue} disabled={!chosen || busy} size="sm" className="w-full h-7 text-[10px]" data-testid="uw-director-continue">
        Continue
      </Button>
    </div>
  );
}


function InlineBsAnalysis({ files, onAnalyseFromMonday, onAnalyseUpload, busy }) {
  const pdfFiles = (files || []).filter((f) => String(f.extension || "").toLowerCase().includes("pdf"));
  const [picked, setPicked] = useState(() => new Set(pdfFiles.map((f) => String(f.id))));
  const fileInputRef = useState(null)[0]; // dummy — we use a ref hook below

  const togglePick = (id) => {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  return (
    <div className="mt-1 ml-5 mb-2 p-2 border rounded bg-emerald-50/30 space-y-1.5" data-testid="uw-inline-bs">
      {pdfFiles.length === 0 ? (
        <>
          <div className="text-[10px] text-muted-foreground">No bank-statement PDFs on this Monday item.</div>
          <UploadBs onUpload={onAnalyseUpload} busy={busy} />
        </>
      ) : (
        <>
          <div className="text-[10px] font-medium text-emerald-700">
            Pick PDFs to analyse ({picked.size}/{pdfFiles.length} selected):
          </div>
          <div className="space-y-1 max-h-32 overflow-auto">
            {pdfFiles.map((f) => {
              const id = String(f.id);
              return (
                <label key={id} className="flex items-center gap-1.5 text-[11px] cursor-pointer">
                  <Checkbox
                    checked={picked.has(id)}
                    onCheckedChange={() => togglePick(id)}
                    data-testid={`uw-bs-file-${id}`}
                  />
                  <span className="truncate flex-1">{f.name}</span>
                </label>
              );
            })}
          </div>
          <div className="flex gap-1.5">
            <Button
              onClick={() => onAnalyseFromMonday(Array.from(picked))}
              disabled={picked.size === 0 || busy}
              size="sm"
              className="flex-1 h-7 text-[10px]"
              data-testid="uw-bs-run-monday"
            >
              {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Play className="w-3 h-3 mr-1" />}
              Analyse {picked.size > 0 ? `(${picked.size})` : ""}
            </Button>
          </div>
          <UploadBs onUpload={onAnalyseUpload} busy={busy} compact />
        </>
      )}
    </div>
  );
}


function UploadBs({ onUpload, busy, compact = false }) {
  const onFilesPicked = (e) => {
    const fl = Array.from(e.target.files || []);
    if (!fl.length) return;
    onUpload(fl);
    e.target.value = ""; // reset
  };
  return (
    <label className={`block text-center cursor-pointer text-[10px] ${compact ? "text-muted-foreground hover:underline mt-1" : ""}`} data-testid="uw-bs-upload-label">
      {compact ? "…or upload PDFs from your computer" : (
        <span className="inline-flex items-center gap-1 px-2 py-1 border rounded bg-white">
          <Download className="w-3 h-3 rotate-180" /> Upload PDFs
        </span>
      )}
      <input
        type="file"
        accept="application/pdf"
        multiple
        className="hidden"
        onChange={onFilesPicked}
        disabled={busy}
        data-testid="uw-bs-upload-input"
      />
    </label>
  );
}


function InlineOfferForm({ isHomeowner, onCalculate, busy }) {
  const [avg, setAvg]         = useState("");
  const [stDebt, setStDebt]   = useState("");
  const [netAssets, setNet]   = useState("");
  const [requested, setReq]   = useState("");
  const [ho, setHo]           = useState(isHomeowner);
  const ready = avg && stDebt !== "";

  return (
    <div className="mt-1 ml-5 mb-2 p-2 border rounded bg-amber-50/30 space-y-1.5" data-testid="uw-inline-offer">
      <div className="text-[10px] font-medium text-amber-700">Offer calculator inputs:</div>
      <div className="grid grid-cols-2 gap-1.5">
        <div>
          <Label className="text-[10px]">Avg monthly sales £</Label>
          <Input type="number" value={avg} onChange={(e) => setAvg(e.target.value)} className="h-7 text-[11px]" data-testid="uw-offer-avg" />
        </div>
        <div>
          <Label className="text-[10px]">ST debt £</Label>
          <Input type="number" value={stDebt} onChange={(e) => setStDebt(e.target.value)} className="h-7 text-[11px]" data-testid="uw-offer-debt" />
        </div>
        <div>
          <Label className="text-[10px]">Net assets £</Label>
          <Input type="number" value={netAssets} onChange={(e) => setNet(e.target.value)} className="h-7 text-[11px]" data-testid="uw-offer-net" />
        </div>
        <div>
          <Label className="text-[10px]">Requested £ (opt)</Label>
          <Input type="number" value={requested} onChange={(e) => setReq(e.target.value)} className="h-7 text-[11px]" data-testid="uw-offer-req" />
        </div>
      </div>
      <label className="flex items-center gap-1.5 text-[11px] cursor-pointer">
        <Checkbox checked={ho} onCheckedChange={setHo} data-testid="uw-offer-ho" />
        Homeowner
      </label>
      <Button
        onClick={() => onCalculate({
          avg_monthly_sales: parseFloat(avg) || 0,
          total_st_debt:     parseFloat(stDebt) || 0,
          net_assets:        parseFloat(netAssets) || 0,
          requested_amount:  requested ? parseFloat(requested) : undefined,
          is_homeowner:      !!ho,
        })}
        disabled={!ready || busy}
        size="sm"
        className="w-full h-7 text-[10px]"
        data-testid="uw-offer-calc"
      >
        {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Calculator className="w-3 h-3 mr-1" />}
        Calculate offer
      </Button>
    </div>
  );
}


function FilesPanel({ files }) {
  const [open, setOpen] = useState(false);
  const n = (files || []).length;
  if (n === 0) return null;
  return (
    <div className="mt-3 border-t pt-2 shrink-0">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 text-xs font-medium text-slate-700 hover:text-slate-900"
        data-testid="uw-files-toggle"
      >
        {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
        <Paperclip className="w-3.5 h-3.5" />
        Monday files ({n})
      </button>
      {open && (
        <div className="mt-2 space-y-1 max-h-44 overflow-auto" data-testid="uw-files-list">
          {files.map((f) => (
            <div key={f.id} className="flex items-center gap-2 px-2 py-1 border rounded text-[11px]">
              <span className="flex-1 truncate">{f.name}</span>
              <a href={f.url} target="_blank" rel="noopener noreferrer">
                <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                  <Download className="w-3 h-3" />
                </Button>
              </a>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
